import { identity, isEmpty } from 'lodash';

import { getActiveIds, getDepthRange } from '@/components/WellLoggingOperations/TabletList/lib';
import api from '@/api';
import { api as apiV2 } from '@/api/api.v2';

import { defaultDisplaySettings } from '@/store/display-settings';

import messages from '@/components/WellLoggingOperations/TabletList/notificationMessages';
import { availableFor, excludedTitles } from '@/components/WellLoggingOperations/Tablet/Flowmetry/constants';
import {
    extractGeoObjectsData,
    formGroupByMeta,
    formTabletByMeta,
    getActiveListItem,
    getMetaDataFromLists,
    getNormalizedResearch,
    getGeoObjectsData,
    getSuccessfulResearches,
    getWellsResearch,
    processingRequestsResults,
    sayAboutEmpty,
    sayAboutErrors,
    reorderSecondArrayWithDifferentKeys,
    getMergedFlatGeoObjects,
    processGroupVersionCleanup,
    getAllListsWellIds,
} from './helpers';
import { flowmetryNormalize } from './normalize/flowmetry';
import { forEach } from 'jszip';
import { mer } from '@/restore/reports/mer';
import { getVersion } from 'valtio';

const PIXELS_PER_CENTIMETER = 38;

const tabletsModel = {
    state: {
        list: {},
        isGroup: false,
        isFgiAllowed: true,
        isTabletsLoading: false,
        isAddCustomGeoVersionActive: false,
        isEditCustomGeoVersionActive: false,
        activeGroupName: undefined,
        activeTabletId: undefined,
        activeGroupIds: [],
        activeListItem: undefined,
        initCustomGeoObjects: [],
        customGeoObjects: [],
        newCustomGeoObjects: [],
        rawGeoObjects: null,
        scale: PIXELS_PER_CENTIMETER / defaultDisplaySettings.ruler.scale,
        wellsMetaData: {},
        reports: {},
        numberCurrentRequests: 0,
        tabletInGroupSelected: false,
        isCustomGeoObjectsChanged: false,
    },
    reducers: {
        'setTabletsScale': (state, payload) => {
            return { ...state, scale: PIXELS_PER_CENTIMETER / payload };
        },
        'setActiveTabletId': (state, payload) => {
            return { ...state, activeTabletId: payload };
        },
        'setActiveGroupIds': (state, payload) => {
            return { ...state, activeGroupIds: payload };
        },
        'setIsFgiAllowed': (state, payload) => {
            return { ...state, isFgiAllowed: payload };
        },
        'setIsCustomGeoObjectsChanged': (state, payload) => {
            return { ...state, isCustomGeoObjectsChanged: payload };
        },
        'addCustomGeoObjects': (state, payload) => {
            const safeState = Array.isArray(state.customGeoObjects) ? state.customGeoObjects : [];
            const safePayload = Array.isArray(payload) ? payload : [];
            for (let i = safePayload.length - 1; i >= 0; i--) {
                const obj = safePayload[i];
                if (
                    safeState.find(
                        object =>
                            object.trueBoredStratumID === obj.trueBoredStratumID && object.versionId === obj.versionId,
                    )
                ) {
                    safePayload.splice(i, 1);
                }
            }

            return { ...state, customGeoObjects: [...safeState, ...safePayload] };
        },
        'setCustomGeoObjects': (state, payload) => {
            return { ...state, customGeoObjects: payload };
        },
        'addInitCustomGeoObjects': (state, payload) => {
            const safeState = Array.isArray(state.initCustomGeoObjects) ? state.initCustomGeoObjects : [];
            const safePayload = Array.isArray(payload) ? payload : [];
            for (let i = safePayload.length - 1; i >= 0; i--) {
                const obj = safePayload[i];
                if (
                    safeState.find(
                        object =>
                            object.trueBoredStratumID === obj.trueBoredStratumID && object.versionId === obj.versionId,
                    )
                ) {
                    safePayload.splice(i, 1);
                }
            }
            return { ...state, initCustomGeoObjects: [...safeState, ...safePayload] };
        },
        'setInitCustomGeoObjects': (state, payload) => {
            return { ...state, initCustomGeoObjects: payload };
        },
        'setNewCustomGeoObjects': (state, payload) => {
            return { ...state, newCustomGeoObjects: payload };
        },
        'setRawGeoObjects': (state, payload) => {
            const { rawGeoObjects, id } = payload;
            return { ...state, rawGeoObjects: { ...state.rawGeoObjects, [id]: rawGeoObjects } };
        },
        'setIsAddCustomGeoVersionActive': (state, payload) => {
            return { ...state, isAddCustomGeoVersionActive: payload };
        },
        'setIsEditCustomGeoVersionActive': (state, payload) => {
            return { ...state, isEditCustomGeoVersionActive: payload };
        },
        'setActiveGroupName': (state, payload) => {
            return { ...state, activeGroupName: payload };
        },
        'setIsGroup': (state, payload) => {
            return { ...state, isGroup: payload };
        },
        'setWellsMetaData': (state, payload) => {
            return { ...state, wellsMetaData: payload };
        },
        'setDepth': (state, payload) => {
            return { ...state, depth: payload };
        },
        'showEmptyTablets': (state, payload) => {
            const { isGroup, wellsMetaData, list } = state;

            const { groupName, activeTabletId } = payload;

            if (groupName) {
                const metaData = Object.entries(wellsMetaData[groupName]);

                const activeGroup = formGroupByMeta(metaData);

                return {
                    ...state,
                    list: {
                        ...list,
                        [groupName]: {
                            ...activeGroup,
                            ...list[groupName],
                        },
                    },
                };
            }

            if (activeTabletId) {
                const metaData = Object.entries(wellsMetaData[activeTabletId]);
                const activeTablet = formTabletByMeta(metaData);

                return {
                    ...state,
                    list: {
                        ...list,
                        [activeTabletId]: { ...activeTablet },
                    },
                };
            }
        },
        'updateTablets': (state, payload) => {
            if (!state) state = this.state;
            const { list, wellsMetaData, activeGroupIds } = state;

            if (!payload) return state;
            const { carottage, wellServices, flowmetry, activeGroupName, activeTabletId, geoObjects, geoObjectsId } =
                payload;

            if (activeGroupName && !geoObjects) {
                const forUpdateState = {
                    ...state,
                    list: {
                        ...list,
                    },
                };
                carottage
                    ? Object.entries(carottage).forEach(gisItem => {
                          const [id, data] = gisItem;

                          forUpdateState.list[activeGroupName] = {
                              ...forUpdateState.list[activeGroupName],
                              [id]: {
                                  ...list?.[activeGroupName]?.[id],
                                  carottage: {
                                      ...list[activeGroupName][id]?.carottage,
                                      columns: data.carottage,
                                  },
                              },
                          };
                      })
                    : null;

                flowmetry
                    ? Object.entries(flowmetry).forEach(flowmetryItem => {
                          const [id, data] = flowmetryItem;

                          const parsedId = Number.parseInt(id);
                          forUpdateState.list[activeGroupName] = {
                              ...forUpdateState.list[activeGroupName],
                              [parsedId]: {
                                  ...forUpdateState.list[activeGroupName][parsedId],
                                  flowmetry: {
                                      ...forUpdateState.list[activeGroupName][parsedId]?.flowmetry,
                                      columns: {
                                          ...forUpdateState.list[activeGroupName][parsedId]?.flowmetry?.columns,
                                          ...data,
                                      },
                                  },
                              },
                          };
                      })
                    : null;

                wellServices
                    ? wellServices.forEach(perforationItem => {
                          // OLD code

                          //   const { id, perforation } = perforationItem;
                          // NEW code
                          const { id, ...data } = perforationItem;
                          const wellTitle = wellsMetaData[activeGroupName][id]?.title;

                          forUpdateState.list[activeGroupName] = {
                              ...forUpdateState.list[activeGroupName],
                              [id]: {
                                  ...list[activeGroupName][id],
                                  title: wellTitle,
                                  wellServices: {
                                      ...list[activeGroupName][id]?.wellServices,
                                      // OLD code
                                      //   data: perforation,
                                      // NEW code
                                      ...data,
                                  },
                              },
                          };
                      })
                    : null;

                return forUpdateState;
            }
            if (geoObjects) {
                if (activeGroupName) {
                    const parsedId = Number.parseInt(geoObjectsId);
                    const geoObjectsColumns = {
                        geoObjects: {
                            ...list[activeGroupName][parsedId]?.geoObjects,
                            versions: [],
                        },
                        lithology: {
                            ...list[activeGroupName][parsedId]?.lithology,
                            versions: [],
                        },
                    };
                    if (!geoObjects[parsedId]?.geoObjects) {
                        return state;
                    }
                    const { lithology, layers } = geoObjects[parsedId].geoObjects;
                    geoObjectsColumns.geoObjects.versions = layers;
                    geoObjectsColumns.lithology.versions = lithology;

                    return {
                        ...state,
                        list: {
                            ...list,
                            [activeGroupName]: {
                                ...list[activeGroupName],
                                [parsedId]: {
                                    ...list[activeGroupName][parsedId],
                                    ...geoObjectsColumns,
                                },
                            },
                        },
                    };
                } else {
                    const geoObjectsColumns = {
                        geoObjects: {
                            ...list[activeTabletId]?.geoObjects,
                            versions: [],
                        },
                        lithology: {
                            ...list[activeTabletId]?.lithology,
                            versions: [],
                        },
                    };

                    if (!geoObjects[activeTabletId]?.geoObjects) {
                        return state;
                    }

                    const { lithology, layers } = geoObjects[activeTabletId].geoObjects;

                    geoObjectsColumns.geoObjects.versions = layers;
                    geoObjectsColumns.lithology.versions = lithology;

                    return {
                        ...state,
                        list: {
                            ...list,
                            [activeTabletId]: {
                                ...list[activeTabletId],
                                ...geoObjectsColumns,
                            },
                        },
                    };
                }
            }

            if (activeTabletId) {
                const wellTitle = wellsMetaData[activeTabletId].title;

                return {
                    ...state,
                    list: {
                        ...list,
                        [activeTabletId]: {
                            ...list[activeTabletId],
                            title: wellTitle,
                            carottage: {
                                ...list[activeTabletId]?.carottage,
                                columns: carottage
                                    ? carottage[activeTabletId]?.carottage
                                    : list[activeTabletId]?.carottage?.columns,
                            },
                            wellServices: {
                                ...list[activeTabletId]?.wellServices,
                                // OLD code
                                // data: wellServices ? wellServices[0]?.perforation : list[activeTabletId]?.wellServices?.data,
                                // NEW code
                                ...(wellServices ? wellServices[0] : {}),
                            },
                            flowmetry: {
                                ...list[activeTabletId]?.flowmetry,
                                columns: {
                                    ...list[activeTabletId]?.flowmetry?.columns,
                                    ...flowmetry?.[activeTabletId],
                                },
                            },
                        },
                    },
                };
            }

            return state;
        },
        'updateDepthRange': (state, payload) => {
            const { list } = state;
            const { depthRange, key } = payload;
            const { id, values: newDepthRange, activeGroupName, activeTabletId } = depthRange;

            if (activeGroupName) {
                const prevDepthRange = list?.[activeGroupName]?.[id]?.depthRange || {};
                const depthValues = [...Object.values(prevDepthRange), ...Object.values(newDepthRange)];

                // !!! Это костыль, но это единсвенный быстрый способ убрать "пустую" глубину
                const prevDepthRangeByKey = list?.[activeGroupName]?.[id]?.[key]?.depthRange || {};
                const depthValuesByKey = [...Object.values(prevDepthRangeByKey), ...Object.values(newDepthRange)];

                return {
                    ...state,
                    list: {
                        ...list,
                        [activeGroupName]: {
                            ...list[activeGroupName],
                            [id]: {
                                ...list?.[activeGroupName]?.[id],
                                [key]: {
                                    ...list?.[activeGroupName]?.[id]?.[key],
                                    depthRange: {
                                        min: Math.min(...depthValuesByKey),
                                        max: Math.max(...depthValuesByKey),
                                    },
                                },
                                depthRange: {
                                    min: Math.min(...depthValues),
                                    max: Math.max(...depthValues),
                                },
                            },
                        },
                    },
                };
            }

            if (activeTabletId) {
                const prevDepthRange = list?.[id]?.depthRange || {};
                const depthValues = [...Object.values(prevDepthRange), ...Object.values(newDepthRange)];

                // !!! Это костыль, но это единсвенный быстрый способ убрать "пустую" глубину
                const prevDepthRangeByKey = list?.[id]?.[key]?.depthRange || {};
                const depthValuesByKey = [...Object.values(prevDepthRangeByKey), ...Object.values(newDepthRange)];

                return {
                    ...state,
                    list: {
                        ...list,
                        [activeTabletId]: {
                            ...list?.[activeTabletId],
                            [key]: {
                                ...list?.[activeTabletId]?.[key],
                                depthRange: {
                                    min: Math.min(...depthValuesByKey),
                                    max: Math.max(...depthValuesByKey),
                                },
                            },
                            depthRange: {
                                min: Math.min(...depthValues),
                                max: Math.max(...depthValues),
                            },
                        },
                    },
                };
            }
            return state;
        },
        'setIsEmpty': (state, payload) => {
            const { list } = state;
            const { emptyWells, name, activeGroupName, activeTabletId } = payload;

            if (activeGroupName) {
                const newListState = { ...list };

                emptyWells.forEach(id => {
                    const updatableTablet = newListState[activeGroupName][id];

                    if (!updatableTablet) return;

                    updatableTablet.notifications = {
                        ...updatableTablet?.notifications,
                        [name]: { ...updatableTablet?.notifications?.[name], isEmpty: true },
                    };

                    newListState[activeGroupName] = { ...newListState[activeGroupName], [id]: { ...updatableTablet } };
                });
                return { ...state, list: newListState };
            }

            if (activeTabletId) {
                const newListState = { ...list };

                emptyWells.forEach(id => {
                    const updatableTablet = list[id];
                    updatableTablet.notifications = {
                        ...updatableTablet?.notifications,
                        [name]: { ...updatableTablet?.notifications?.[id], isEmpty: true },
                    };

                    newListState[id] = { ...updatableTablet };
                });
                return { ...state, list: newListState };
            }
            return state;
        },
        'setErrors': (state, payload) => {
            const { list } = state;
            const { rejectedIds, name, activeGroupName, activeTabletId } = payload;

            if (activeGroupName) {
                const newListState = { ...list };

                rejectedIds.forEach(id => {
                    const updatableTablet = list[activeGroupName][id];

                    if (!updatableTablet) return;

                    updatableTablet.notifications = {
                        ...updatableTablet?.notifications,
                        [name]: { ...updatableTablet?.notifications?.[name], isError: true },
                    };

                    newListState[activeGroupName] = { ...newListState[activeGroupName], [id]: { ...updatableTablet } };
                });
                return { ...state, list: newListState };
            }

            if (activeTabletId) {
                const newListState = { ...list };

                rejectedIds.forEach(id => {
                    const updatableTablet = list[id];
                    updatableTablet.notifications = {
                        ...updatableTablet.notifications,
                        [name]: { ...updatableTablet?.notifications?.[id], isError: true },
                    };

                    newListState[id] = { ...updatableTablet };
                });
                return { ...state, list: newListState };
            }
            return state;
        },
        'setWorkWithGroup': (state, payload) => {
            const { groupName, groupIds } = payload;
            return {
                ...state,
                activeTabletId: undefined,
                isGroup: true,
                activeGroupName: groupName,
                activeGroupIds: groupIds,
            };
        },
        'setWorkWithWell': (state, payload) => {
            return {
                ...state,
                activeTabletId: payload,
                isGroup: false,
                activeGroupName: undefined,
                activeGroupIds: [],
            };
        },
        'setIsTabletsLoading': (state, payload) => {
            return {
                ...state,
                isTabletsLoading: payload,
            };
        },
        'cacheSuccessfullyReports': (state, payload) => {
            const { isGroup, activeGroupName, activeTabletId, reports } = state;

            if (isGroup) {
                return { ...state, reports: { ...reports, [activeGroupName]: payload } };
            }

            if (activeTabletId) {
                return { ...state, reports: { ...reports, [activeTabletId]: payload } };
            }
        },
        'updateList': (state, payload) => {
            return { ...state, list: { ...payload } };
        },
        'changeColumnsVisible': (state, payload) => {
            const { isGroup, activeGroupName, list } = state;
            const [targetListItem] = payload;
            const { id, columns } = targetListItem;

            const newColumnsState = columns.map(column => {
                const { keyTitle, isVisible } = column;

                return {
                    keyTitle,
                    isVisible,
                };
            });

            if (isGroup) {
                const editWell = list[activeGroupName][id];
            }

            return state;
        },
        'toggleColumnVisibility': (state, payload) => {
            const { isGroup, activeGroupName, activeTabletId, list } = state;

            if (isEmpty(payload)) return state;

            const columns = Object.entries(payload);

            const updatableObject = isGroup ? list[activeGroupName] : list[activeTabletId];

            columns.forEach(column => {
                const [wellId, columnVisibilityState] = column;

                Object.entries(columnVisibilityState).forEach(column => {
                    const [keyTitle, visible] = column;

                    if (isGroup) {
                        updatableObject[wellId] = {
                            ...updatableObject[wellId],
                            [keyTitle]: {
                                ...updatableObject[wellId][keyTitle],
                                visible,
                            },
                        };
                    } else {
                        updatableObject[keyTitle] = {
                            ...updatableObject[keyTitle],
                            visible,
                        };
                    }
                });
            });

            if (isGroup) {
                return { ...state, list: { ...list, [activeGroupName]: { ...updatableObject } } };
            }
            if (activeTabletId) {
                return { ...state, list: { ...list, [activeTabletId]: { ...updatableObject } } };
            }

            return state;
        },
        'incrementingCurrentRequests': (state, payload) => {
            const { numberCurrentRequests } = state;

            return { ...state, numberCurrentRequests: numberCurrentRequests + 1 };
        },
        'decrementingCurrentRequests': (state, payload) => {
            const { numberCurrentRequests } = state;

            return { ...state, numberCurrentRequests: numberCurrentRequests - 1 };
        },
        'inspector/setDepth': (state, payload) => {
            return { ...state, depth: payload };
        },
        'toggleTabletInGroupSelected': (state, payload) => {
            return { ...state, tabletInGroupSelected: payload };
        },
    },
    effects: dispatch => ({
        async initSettings(payload, rootState) {
            this.setIsTabletsLoading(true);
            const { lists, tablets } = rootState;

            const { activeItemKey, data: listItems } = lists;

            const { list, wellsMetaData } = tablets;

            const activeListItem = getActiveListItem(listItems, activeItemKey);
            const isGroupActive = activeListItem?.node === 'group';
            const isWellActive = activeListItem?.node === 'well' || activeListItem?.node === 'shaft';
            const groupName = activeListItem?.key;
            const groupIds = isGroupActive && getActiveIds(activeListItem, activeItemKey);
            const stateIds = list[groupName] ? Object.keys(list[groupName]).map(item => +item) : [];
            isGroupActive ? this.setWorkWithGroup({ groupName, groupIds }) : null;

            if (isGroupActive) {
                const missingIds = [];
                groupIds.forEach(id => {
                    if (!stateIds.includes(id)) {
                        missingIds.push(id);
                    }
                });

                const metaDataFromList = getMetaDataFromLists(activeListItem);
                this.setWellsMetaData({ ...wellsMetaData, [groupName]: metaDataFromList });
                const missingResearchItems = missingIds.map(id => {
                    const listItem = metaDataFromList[id];

                    return { id: listItem.id, key: listItem.key };
                });

                const missingItems = missingIds.map(id => {
                    const listItem = metaDataFromList[id];

                    return listItem.id;
                });

                if (missingResearchItems.length === 0 && missingItems.length === 0) return;

                this.showEmptyTablets({ groupName });
                // легаси
                // dispatch.tablets.getResearches({ ids: missingResearchItems, groupName });
                // this.incrementingCurrentRequests();
                dispatch.tablets.getPerforation({ ids: missingItems, groupName });
                this.incrementingCurrentRequests();
                for (const wellId of groupIds) {
                    dispatch.stratigraphy.loadStratigraphy(wellId);
                    dispatch.tech.loadTech(wellId);
                }

                dispatch.tablets.getGeoObjects({ ids: groupIds, groupName });
                this.incrementingCurrentRequests();

                dispatch.tablets.getFlowmetry({ ids: missingItems, groupName });

                this.getGrp({ ids: groupIds, groupName });
            }

            if (isWellActive) {
                const activeTabletId = activeListItem.id;
                this.setWorkWithWell(activeTabletId);
                const metaDataFromList = getMetaDataFromLists(activeListItem);
                this.setWellsMetaData({ ...wellsMetaData, ...metaDataFromList });
                // const targetItem = Object.values(metaDataFromList);
                if (list[activeTabletId]) return;
                this.showEmptyTablets({ activeTabletId });
                // легаси
                // dispatch.tablets.getResearches({ ids: targetItem, activeTabletId });
                // this.incrementingCurrentRequests();
                if (
                    !tablets.list[activeTabletId]?.wellServices?.perforation ||
                    !tablets.list[activeTabletId]?.wellServices?.isolation
                ) {
                    dispatch.tablets.getPerforation({ ids: [activeTabletId], activeTabletId });
                    this.incrementingCurrentRequests();
                }
                dispatch.tablets.getGeoObjects({ ids: [activeTabletId], activeTabletId });
                this.incrementingCurrentRequests();
                dispatch.stratigraphy.loadStratigraphy(activeTabletId);
                dispatch.tech.loadTech(activeTabletId);

                dispatch.tablets.getFlowmetry({ ids: [activeTabletId], activeTabletId });
                this.getGrp({ ids: [activeTabletId], activeTabletId });
            }
            this.setIsTabletsLoading(false);
        },
        getResearches(payload, rootState) {
            this.setIsTabletsLoading(true);
            const { ids, groupName, activeTabletId } = payload;
            const { lists } = rootState;

            const { activeItemKey, data: listItems } = lists;

            const activeListItem = getActiveListItem(listItems, activeItemKey);

            const metaDataFromList = getMetaDataFromLists(activeListItem);

            getWellsResearch(ids, dispatch.settingsResearch.loadResearch)
                .then(results => {
                    const { successWells, emptyWells, rejectedIds } = processingRequestsResults(results);
                    sayAboutEmpty({
                        emptyWells,
                        wellsMetaData: metaDataFromList,
                        messages,
                        name: messages.researches.type,
                    });
                    this.setIsEmpty({
                        emptyWells,
                        name: messages.researches.type,
                        activeGroupName: groupName,
                        activeTabletId,
                    });
                    sayAboutErrors({
                        rejectedIds,
                        wellsMetaData: metaDataFromList,
                        messages,
                        name: messages.researches.type,
                    });
                    this.setErrors({
                        rejectedIds,
                        name: messages.researches.type,
                        activeGroupName: groupName,
                        activeTabletId,
                    });
                    return { results, successWells };
                })
                .then(({ results, successWells }) => {
                    const researches = getSuccessfulResearches(results, successWells);

                    researches.forEach(research => {
                        const { id, response } = research;
                        const depthRange = getDepthRange(response);

                        const depthValues = [...Object.values(depthRange)];

                        this.updateDepthRange({
                            depthRange: {
                                activeGroupName: groupName,
                                activeTabletId,
                                id,
                                values: {
                                    min: Math.min(...depthValues),
                                    max: Math.max(...depthValues),
                                },
                            },
                            key: 'carottage',
                        });
                    });

                    return successWells;
                })
                .then(successWells =>
                    getNormalizedResearch(
                        successWells.map(item => item.key),
                        dispatch.settings.prepareNormalizedDataForTablet,
                    ),
                )
                .then(carottage => {
                    this.updateTablets({ activeGroupName: groupName, activeTabletId, carottage });
                    // this.decrementingCurrentRequests();
                });
            this.setIsTabletsLoading(false);
        },
        getPerforation(payload, rootState) {
            const { ids, groupName, activeTabletId } = payload;
            const { lists } = rootState;

            const { activeItemKey, data: listItems } = lists;

            const activeListItem = getActiveListItem(listItems, activeItemKey);

            const metaDataFromList = getMetaDataFromLists(activeListItem);

            const chartsJson = JSON.stringify(['perforation', 'isolation']);

            Promise.allSettled(ids.map(id => apiV2.carottage.fetchGraphData(id, chartsJson))).then(perforation => {
                const success = [];
                const emptyWells = [];
                const rejectedIds = [];
                const perforationData = [];

                /*
                    Может ли изоляция существовать без перфорации?
                    При запросе обеих сущностей вносить ответ на вопрос надо где то ниже.
                */

                /*
                    TODO: Object.values - лучше сделать один раз, а не каждом шаге проверки.
                */
                perforation.forEach((perforationItem, index) => {
                    if (
                        perforationItem.status === 'fulfilled' &&
                        (Object.values(perforationItem.value.perforation).length !== 0 ||
                            Object.values(perforationItem.value.isolation).length !== 0)
                    ) {
                        success.push(ids[index]);
                        perforationData.push({
                            id: ids[index],
                            perforation: perforationItem.value.perforation,
                            isolation: perforationItem.value.isolation,
                        });
                    }

                    if (
                        perforationItem.status === 'fulfilled' &&
                        Object.values(perforationItem.value.perforation).length === 0 &&
                        Object.values(perforationItem.value.isolation).length === 0
                    ) {
                        emptyWells.push(ids[index]);
                    }

                    if (perforationItem.status === 'rejected') {
                        rejectedIds.push(ids[index]);
                    }
                });

                sayAboutEmpty({
                    emptyWells,
                    wellsMetaData: metaDataFromList,
                    messages,
                    name: messages.perforation.type,
                });
                this.setIsEmpty({
                    emptyWells,
                    name: messages.perforation.type,
                    activeGroupName: groupName,
                    activeTabletId,
                });
                sayAboutErrors({
                    rejectedIds,
                    wellsMetaData: metaDataFromList,
                    messages,
                    name: messages.perforation.type,
                });
                this.setErrors({
                    rejectedIds,
                    name: messages.perforation.type,
                    activeGroupName: groupName,
                    activeTabletId,
                });

                /*
                    По итогу получения данных о перфорации, дальнейший шаг - это сбор информации о глубинах.
                    Собираются данные о глубинах из перфорации и изоляции.

                    Ищется минимальное и максимальное значение, которые добавляется в общую глубину.

                    TODO: эта логика требует стандартизации, так как выполняется на каждом другом шаге после получения данных.
                */
                perforationData.forEach(perforationItem => {
                    const { id, perforation, isolation } = perforationItem;

                    if (Object.values(perforation).length === 0 && Object.values(isolation).length === 0) return;

                    const depthPerforations = getDepthFromData(perforation);
                    const depthIsolations = getDepthFromData(isolation);

                    const mergeDepth = [...depthPerforations, ...depthIsolations];
                    /*
                        Если по итогу значение глубины будет пустым (пустой массив), то выходим из итерации,
                        так как Math.min и Math.max дадут значение infinity.
                    */
                    if (isEmpty(mergeDepth)) return;

                    this.updateDepthRange({
                        depthRange: {
                            activeGroupName: groupName,
                            activeTabletId,
                            id,
                            values: {
                                min: Math.floor(Math.min(...mergeDepth)),
                                max: Math.ceil(Math.max(...mergeDepth)),
                            },
                        },
                        key: 'perforation',
                    });
                });

                this.updateTablets({ activeGroupName: groupName, activeTabletId, wellServices: perforationData });
                this.decrementingCurrentRequests();
            });
        },
        async getGeoObjects(payload, rootState) {
            const { ids, groupName, activeTabletId, customGeoObjectsAction } = payload;

            const { tablets, inspector, lists } = rootState;
            const { geoObjects: geoVersions } = inspector;

            const { customGeoObjects, list } = tablets;
            let filteredIds = ids;
            if (customGeoObjectsAction) {
                filteredIds = [];
                dispatch.inspector.setIsCustomVersionsLoading(true);
                const allListsWellsIds = getAllListsWellIds(lists);
                ids.forEach(id => {
                    if (allListsWellsIds.includes(id)) filteredIds.push(id);
                });
            }

            try {
                const geoObjectsData = await getGeoObjectsData(filteredIds);

                const { geoObjects, versions, customVersions } = geoObjectsData;

                if (customVersions.length > 0) {
                    customVersions.forEach(ver => {
                        ver.isCustom = true;
                        ver.wells_id = [...new Set(ver.wells_id)];

                        dispatch.inspector.addCustomGeoVersion({
                            customGeoObject: ver,
                            wellsIds: ver.wells_id,
                            customGeoObjectsAction,
                        });
                    });
                }

                const mergedVersions = [...customVersions, ...versions];

                const flatGeoObjects = geoObjects.flat(3);

                const numberedVersions = mergedVersions.flat().map((version, i) => {
                    version.number = i + 1;
                    version.visible = i === 0;
                    if (!version.isCustom) version.wells_id = [...new Set(version.wells_id)];
                    return version;
                });

                const mergedFlatGeoObjects = getMergedFlatGeoObjects(customVersions, flatGeoObjects);

                dispatch.inspector.getGeoObjectsList(numberedVersions, this.state);
                const mergedGeoObjects = Object.values(
                    mergedFlatGeoObjects.reduce((acc, obj) => {
                        if (acc[obj.wellId]) {
                            acc[obj.wellId].children = acc[obj.wellId].children.concat(obj.children);
                        } else {
                            acc[obj.wellId] = { ...obj };
                        }
                        return acc;
                    }, {}),
                );

                for (const geoObject of mergedGeoObjects) {
                    geoObject.children = geoObject.children.filter(
                        version => !version?.stratumVersionDescription?.includes('Первичный РИГИС'),
                    );
                }

                this.cacheSuccessfullyReports(mergedGeoObjects);

                const extractedData = extractGeoObjectsData(
                    mergedGeoObjects,
                    numberedVersions,
                    this.addCustomGeoObjects,
                    this.addInitCustomGeoObjects,
                    customGeoObjects,
                    customVersions,
                );

                const activeId = Number.isNaN(activeTabletId) ? ids[0] : activeTabletId;
                Object.entries(extractedData).forEach(([id, geoObjects]) => {
                    const { geoObjects: versions } = geoObjects;
                    if (versions && isEmpty(versions)) return;
                    const { lithology, layers } = versions;

                    const lithologyDepth = lithology.flatMap(item => {
                        const { data } = item;
                        return data.flatMap(entry =>
                            [entry.boredSeamBottom, entry.boredSeamRoof].filter(v => v !== null),
                        );
                    });

                    const layersDepth = layers.flatMap(item => {
                        const { data } = item;
                        return data.flatMap(entry =>
                            [entry.boredStratumBottom, entry.boredStratumRoof].filter(v => v !== null),
                        );
                    });

                    const totalDepth = [...lithologyDepth, ...layersDepth];

                    if (!isEmpty(totalDepth)) {
                        this.updateDepthRange({
                            depthRange: {
                                activeGroupName: groupName,
                                activeId,
                                id,
                                values: {
                                    min: Math.floor(Math.min(...totalDepth)),
                                    max: Math.ceil(Math.max(...totalDepth)),
                                },
                            },
                            key: 'geoObjects',
                        });
                    }

                    if (geoVersions[id] && groupName) {
                        if (extractedData[id].geoObjects.layers[0].number > 1) {
                            extractedData[id].geoObjects.layers = extractedData[id].geoObjects.layers.map(
                                (layer, i) => {
                                    layer.visible = i === 0;
                                    return layer;
                                },
                            );
                        }
                        this.updateTablets({
                            activeGroupName: '',
                            activeTabletId: id,
                            geoObjects: extractedData,
                            geoObjectsId: id,
                        });
                    }
                    this.updateTablets({
                        activeGroupName: groupName,
                        activeTabletId,
                        geoObjects: extractedData,
                        geoObjectsId: id,
                    });

                    Object.keys(list).forEach(key => {
                        if (customVersions.length > 0) {
                            customVersions.forEach(version => {
                                processGroupVersionCleanup(version, list, extractedData, lists, dispatch);
                                version.wells_id.forEach(wellId => {
                                    if (list[key][wellId]) {
                                        if (
                                            list[key][wellId]?.geoObjects?.versions &&
                                            extractedData[wellId]?.geoObjects?.layers
                                        ) {
                                            extractedData[wellId].geoObjects.layers = extractedData[
                                                wellId
                                            ].geoObjects.layers.map((layer, i) => {
                                                layer.visible = i === 0;
                                                return layer;
                                            });

                                            dispatch.tablets.updateTablets({
                                                activeGroupName: key,
                                                geoObjects: extractedData,
                                                geoObjectsId: wellId,
                                            });
                                        }
                                    }
                                    if (
                                        list[wellId]?.geoObjects?.versions &&
                                        extractedData[wellId]?.geoObjects?.layers
                                    ) {
                                        extractedData[wellId].geoObjects.layers = extractedData[
                                            wellId
                                        ].geoObjects.layers.map((layer, i) => {
                                            layer.visible = i === 0;
                                            return layer;
                                        });

                                        dispatch.tablets.updateTablets({
                                            activeTabletId: wellId,
                                            geoObjects: extractedData,
                                            geoObjectsId: id,
                                        });
                                    }

                                    if (!isEmpty(totalDepth)) {
                                        this.updateDepthRange({
                                            depthRange: {
                                                activeGroupName: key,
                                                wellId,
                                                id,
                                                values: {
                                                    min: Math.floor(Math.min(...totalDepth)),
                                                    max: Math.ceil(Math.max(...totalDepth)),
                                                },
                                            },
                                            key: 'geoObjects',
                                        });
                                    }
                                });
                            });
                        } else {
                            if (list[key][id]) {
                                dispatch.tablets.updateTablets({
                                    activeGroupName: key,
                                    geoObjects: extractedData,
                                    geoObjectsId: id,
                                });
                            }
                        }
                    });
                });
            } catch (error) {
                console.error('Error in getGeoObjects:', error);
            } finally {
                if (!customGeoObjectsAction) this.decrementingCurrentRequests();
                dispatch.inspector.setIsCustomVersionsLoading(false);
            }
        },

        getFlowmetry(payload) {
            const { ids, groupName, activeTabletId } = payload;

            Promise.allSettled(
                ids.map(async id => {
                    const result = await api.carottage.fetchFlowMeteringData(id);
                    return { data: result, wellId: id };
                }),
            ).then(metering => {
                metering.forEach(item => {
                    const { status, value } = item;

                    if (status === 'rejected') return;

                    const { data, wellId } = value;

                    const { PGITable, PGIColumn } = data;

                    /*
                        В этой точке мы получаем информация о потокометрии.
                        Поэтому есть возможность забрать необходимые табличные данные (ПГИ данные), которые лежат в этом же запросе.

                        Эти данные попадают в другую модель, где лежат в виде [wellID]: [PGI data]
                    */
                    dispatch.pgiWell.setPGIEffect({ wellID: wellId, data: PGITable });

                    Object.entries(PGIColumn).forEach(research => {
                        const [researchId, columnData] = research;

                        const flowmetry = {};
                        const depths = [];

                        const {
                            header: { date, aim, regimes, dynamic_level, productivity_rate, title },
                            data,
                        } = columnData;

                        /*
                         * Зачем создавать сложную структуру сейчас, если в коде ниже неудобно с ней работать?
                         */
                        flowmetry[wellId] = {
                            [researchId]: {
                                visible: true,
                                header: {
                                    date,
                                    aim,
                                    regimes,
                                    roof: 0,
                                    bottom: 0,
                                    dynamic_level,
                                    productivity_rate,
                                    title,
                                },
                            },
                        };

                        data.forEach(researchType => {
                            const { title, ...otherResearchProperties } = researchType;

                            const { roof, bottom } = otherResearchProperties;

                            if (roof || roof === 0) depths.push(roof);
                            if (bottom || bottom === 0) depths.push(bottom);

                            Object.entries(availableFor).forEach(column => {
                                const [columnName, availableDataType] = column;

                                if (availableDataType.includes(title)) {
                                    const researchList = flowmetry?.[wellId]?.[researchId]?.[columnName]?.[title] || [];

                                    flowmetry[wellId] = {
                                        [researchId]: {
                                            ...flowmetry?.[wellId]?.[researchId],
                                            [columnName]: {
                                                ...flowmetry?.[wellId]?.[researchId]?.[columnName],
                                                [title]: [...researchList, otherResearchProperties],
                                            },
                                        },
                                    };
                                } else if (columnName === 'secondColumn' && !excludedTitles.includes(title)) {
                                    const researchList = flowmetry?.[wellId]?.[researchId]?.[columnName]?.[title] || [];

                                    flowmetry[wellId] = {
                                        [researchId]: {
                                            ...flowmetry?.[wellId]?.[researchId],
                                            [columnName]: {
                                                ...flowmetry?.[wellId]?.[researchId]?.[columnName],
                                                [title]: [...researchList, otherResearchProperties],
                                            },
                                        },
                                    };
                                }
                            });
                        });

                        /*
                            * Старый код поиска мин и макс значений:

                            Открытый вопрос: зачем фильтровать глубины, если по коду выше falsy значения не попадут в результирующий список

                            min: Math.floor(Math.min(...depths.filter(item => item !== undefined))),
                            max: Math.ceil(Math.max(...depths.filter(item => item !== undefined))),
                        */

                        /*
                            Значение минимальной и максимальной глубины используются в двух местах:
                            * 1. Это поиск интервала, который выводится в шапке данной колонки
                            * 2. Это значения, который уходят в пересчет общей глубины скважины (должны ли они это делать сразу?)
                        */

                        let minDepth;
                        let maxDepth;

                        if (depths.length > 0) {
                            minDepth = Math.min(...depths);
                            maxDepth = Math.max(...depths);

                            flowmetry[wellId][researchId].header.roof = minDepth;
                            flowmetry[wellId][researchId].header.bottom = maxDepth;
                        }

                        this.updateTablets({ activeGroupName: groupName, activeTabletId, flowmetry });

                        /*
                            TODO: mb fix

                            По логам, this.updateDepthRange вызывается на каждой итерации forEach, что выше.
                            Это приводит к лишним перерисовкам.
                            Возможно, надо подготовить данные по глубинам отдельно и вызвать updateDepthRange с уже общими
                            метками глубин.
                        */

                        depths.length > 0 &&
                            this.updateDepthRange({
                                depthRange: {
                                    activeGroupName: groupName,
                                    activeTabletId,
                                    id: wellId,
                                    values: {
                                        min: Math.floor(minDepth),
                                        max: Math.ceil(maxDepth),
                                    },
                                },
                                key: 'flowmetry',
                            });
                    });
                });
            });
        },
        /*
            Запуск получения ГРП.
            * Сам запрос, как и данные, находятся в другой модели - grpWell.
            * Сюда запрос вернет данные о глубинах ПГР, запустив логику обновления ОБЩЕЙ глубины с помощью метода updateDepthRange.

            response имеет вид:
            {
                [wellId]: depthValues[number]
            }
        */
        getGrp(payload) {
            const { ids, groupName, activeTabletId } = payload;

            dispatch.grpWell.getGrpWell(ids).then(response => {
                for (const [wellId, depthValues] of Object.entries(response)) {
                    if (!depthValues.length) return;

                    this.updateDepthRange({
                        depthRange: {
                            activeGroupName: groupName,
                            activeTabletId,
                            id: wellId,
                            values: {
                                min: Math.floor(Math.min(...depthValues)),
                                max: Math.ceil(Math.max(...depthValues)),
                            },
                        },
                        key: 'grp',
                    });
                }
            });
        },
        setOnlyOneVersionVisibleGeoObjects(payload, rootState) {
            const { versionId } = payload;

            const { tablets } = rootState;
            const { isGroup, activeGroupName, activeTabletId, activeGroupIds, list } = tablets;
            const geoObjects = {};

            if (isGroup) {
                activeGroupIds.forEach(wellId => {
                    const parsedId = Number.parseInt(wellId);
                    const tablet = list?.[activeGroupName][parsedId];
                    const layersArr = tablet.geoObjects.versions.map(version =>
                        version?.versionId === versionId
                            ? { ...version, visible: true }
                            : { ...version, visible: false },
                    );
                    const lithology = tablet.lithology.versions.map(version =>
                        version?.versionId === versionId
                            ? { ...version, visible: true }
                            : { ...version, visible: false },
                    );

                    geoObjects[parsedId] = { geoObjects: { lithology: lithology, layers: layersArr } };

                    this.updateTablets({ activeGroupName, activeTabletId, geoObjects, geoObjectsId: parsedId });
                });
            }

            if (activeTabletId) {
                const activeTablet = list?.[activeTabletId];
                const layersArr = activeTablet.geoObjects.versions.map(version =>
                    version?.versionId === versionId ? { ...version, visible: true } : { ...version, visible: false },
                );
                const lithology = activeTablet.lithology.versions.map(version =>
                    version?.versionId === versionId ? { ...version, visible: true } : { ...version, visible: false },
                );

                geoObjects[activeTabletId] = { geoObjects: { lithology: lithology, layers: layersArr } };

                this.updateTablets({ activeGroupName, activeTabletId, geoObjects });
            }
        },
        setTabletGeoObjects(payload, rootState) {
            const { versions } = payload;
            const { tablets } = rootState;
            const { isGroup, activeGroupName, activeTabletId, activeGroupIds, reports, list } = tablets;
            const geoObjects = {};

            if (activeTabletId) {
                const activeTablet = list?.[activeTabletId];
                if (activeTablet?.geoObjects?.versions) {
                    const reorderedGeoObjects = reorderSecondArrayWithDifferentKeys(
                        versions,
                        activeTablet.geoObjects.versions,
                        'stratumVersionId',
                        'versionId',
                    );
                    const reorderedLithology = reorderSecondArrayWithDifferentKeys(
                        versions,
                        activeTablet.lithology.versions,
                        'stratumVersionId',
                        'versionId',
                    );

                    geoObjects[activeTabletId] = {
                        geoObjects: { lithology: reorderedLithology, layers: reorderedGeoObjects },
                    };
                    this.updateTablets({ activeGroupName, activeTabletId, geoObjects });
                }
            } else {
                activeGroupIds.forEach(wellId => {
                    const parsedId = Number.parseInt(wellId);
                    const tablet = list?.[activeGroupName][parsedId];
                    if (tablet?.geoObjects?.versions) {
                        const reorderedGeoObjects = reorderSecondArrayWithDifferentKeys(
                            versions,
                            tablet.geoObjects.versions,
                            'stratumVersionId',
                            'versionId',
                        );
                        const reorderedLithology = reorderSecondArrayWithDifferentKeys(
                            versions,
                            tablet.lithology.versions,
                            'stratumVersionId',
                            'versionId',
                        );

                        geoObjects[parsedId] = {
                            geoObjects: { lithology: reorderedLithology, layers: reorderedGeoObjects },
                        };

                        this.updateTablets({ activeGroupName, activeTabletId, geoObjects, geoObjectsId: parsedId });
                    }
                });
            }
        },

        toggleGeoObjectsVisible(payload, rootState) {
            const { versionId, visible } = payload;

            const { tablets } = rootState;
            const { isGroup, activeGroupName, activeTabletId, activeGroupIds, reports, list } = tablets;
            const geoObjects = {};

            if (isGroup) {
                activeGroupIds.forEach(wellId => {
                    const parsedId = Number.parseInt(wellId);
                    const tablet = list?.[activeGroupName][parsedId];
                    if (tablet?.geoObjects?.versions) {
                        const layersArr = tablet.geoObjects.versions.map(version =>
                            version?.versionId === versionId ? { ...version, visible: visible } : version,
                        );
                        const lithology = tablet.lithology.versions.map(version =>
                            version?.versionId === versionId ? { ...version, visible: visible } : version,
                        );

                        geoObjects[parsedId] = { geoObjects: { lithology: lithology, layers: layersArr } };

                        this.updateTablets({ activeGroupName, activeTabletId, geoObjects, geoObjectsId: parsedId });
                    }
                });
            }

            if (activeTabletId) {
                const activeTablet = list?.[activeTabletId];
                const layersArr = activeTablet.geoObjects?.versions.map(version =>
                    version?.versionId === versionId ? { ...version, visible: visible } : version,
                );
                const lithology = activeTablet.lithology?.versions?.map(version =>
                    version?.versionId === versionId ? { ...version, visible: visible } : version,
                );

                geoObjects[activeTabletId] = { geoObjects: { lithology: lithology, layers: layersArr } };

                this.updateTablets({ activeGroupName, activeTabletId, geoObjects });
            }
        },
        toggleFlowmetryVisibleAll(payload, rootState) {
            const { wellId, visible } = payload;
            const { tablets } = rootState;
            const { isGroup, activeGroupName, activeTabletId, list } = tablets;
            const wellData = {
                activeGroupName,
                activeTabletId,
                flowmetry: { [wellId]: {} },
            };

            if (isGroup) {
                const flowmetryIds = Object.keys(list[activeGroupName]?.[wellId]?.flowmetry?.columns);
                flowmetryIds.forEach(flowmetryId => {
                    const { ...otherData } = getPreviousFlowmetryResearch({
                        tabletList: list,
                        activeGroupName,
                        wellId,
                        flowmetryId,
                    });
                    wellData.flowmetry[wellId][flowmetryId] = { ...otherData, visible };
                });
                this.updateTablets(wellData);
            }

            if (activeTabletId) {
                const flowmetryIds = Object.keys(list[wellId]?.flowmetry?.columns);
                flowmetryIds.forEach(flowmetryId => {
                    const { ...otherData } = getPreviousFlowmetryResearch({
                        tabletList: list,
                        wellId,
                        flowmetryId,
                    });
                    wellData.flowmetry[wellId][flowmetryId] = { ...otherData, visible };
                });
                this.updateTablets(wellData);
            }
        },

        toggleFlowmetryVisible(payload, rootState) {
            const { wellId, flowmetryId, visible } = payload;
            const { tablets } = rootState;
            const { isGroup, activeGroupName, activeTabletId, list } = tablets;

            if (isGroup) {
                const { visible, header, ...otherData } = getPreviousFlowmetryResearch({
                    tabletList: list,
                    activeGroupName,
                    wellId,
                    flowmetryId,
                });

                this.updateTablets({
                    activeGroupName,
                    activeTabletId,
                    flowmetry: { [wellId]: { [flowmetryId]: { visible: !visible, header, ...otherData } } },
                });
            }

            if (activeTabletId) {
                const { visible, header, ...otherData } = getPreviousFlowmetryResearch({
                    tabletList: list,
                    wellId,
                    flowmetryId,
                });

                this.updateTablets({
                    activeGroupName,
                    activeTabletId,
                    flowmetry: { [activeTabletId]: { [flowmetryId]: { visible: !visible, header, ...otherData } } },
                });
            }
        },

        toggleFlowmetryVisiblePgi(payload, rootState) {
            const { wellId, isVisiblePGIOnly } = payload;
            const { tablets } = rootState;
            const { isGroup, activeGroupName, activeTabletId, list } = tablets;
            const wellData = {
                activeGroupName,
                activeTabletId,
                flowmetry: { [wellId]: {} },
            };

            if (isGroup) {
                const flowmetryIds = Object.keys(list[activeGroupName]?.[wellId]?.flowmetry?.columns ?? {});
                if (flowmetryIds.length === 0) {
                    return;
                }
                // const isEmpty = !flowmetryIds
                //     .map(flowmetryId => {
                //         const { visible } = getPreviousFlowmetryResearch({
                //             tabletList: list,
                //             activeGroupName,
                //             wellId,
                //             flowmetryId,
                //         });
                //         return visible;
                //     })
                //     .includes(true);
                const isEmpty = true;
                if (isVisiblePGIOnly) {
                    flowmetryIds.forEach(flowmetryId => {
                        const { visible = false, ...otherData } =
                            getPreviousFlowmetryResearch({
                                tabletList: list,
                                activeGroupName,
                                wellId,
                                flowmetryId,
                            }) ?? {};
                        let newVisible = visible;
                        if (isEmpty) {
                            if (((otherData?.secondColumn ?? {})?.['Фильтрация'] ?? []).length !== 0) {
                                newVisible = true;
                            } else {
                                newVisible = false;
                            }
                        } else if (visible) {
                            if (((otherData?.secondColumn ?? {})?.['Фильтрация'] ?? []).length === 0) {
                                newVisible = false;
                            }
                        }
                        wellData.flowmetry[wellId][flowmetryId] = { ...otherData, visible: newVisible };
                    });
                    this.updateTablets(wellData);
                } else {
                    flowmetryIds.forEach(flowmetryId => {
                        const { visible = false, ...otherData } =
                            getPreviousFlowmetryResearch({
                                tabletList: list,
                                activeGroupName,
                                wellId,
                                flowmetryId,
                            }) ?? {};
                        let newVisible = visible;
                        if (isEmpty) {
                            newVisible = true;
                        }
                        wellData.flowmetry[wellId][flowmetryId] = { ...otherData, visible: newVisible };
                    });
                    this.updateTablets(wellData);
                }
            }

            if (activeTabletId) {
                const flowmetryIds = Object.keys(list[wellId]?.flowmetry?.columns ?? {});
                if (flowmetryIds.length === 0) {
                    return;
                }

                // const isEmpty = !flowmetryIds
                //     .map(flowmetryId => {
                //         const { visible } = getPreviousFlowmetryResearch({
                //             tabletList: list,
                //             wellId,
                //             flowmetryId,
                //         });
                //         return visible;
                //     })
                //     .includes(true);

                const isEmpty = true;

                if (isVisiblePGIOnly) {
                    flowmetryIds.forEach(flowmetryId => {
                        const { visible, ...otherData } = getPreviousFlowmetryResearch({
                            tabletList: list,
                            wellId,
                            flowmetryId,
                        });

                        let newVisible = visible;
                        if (isEmpty) {
                            if (((otherData?.secondColumn ?? {})?.['Фильтрация'] ?? []).length !== 0) {
                                newVisible = true;
                            } else {
                                newVisible = false;
                            }
                        } else if (visible) {
                            if (((otherData?.secondColumn ?? {})?.['Фильтрация'] ?? []).length === 0) {
                                newVisible = false;
                            }
                        }

                        wellData.flowmetry[wellId][flowmetryId] = { ...otherData, visible: newVisible };
                    });
                    this.updateTablets(wellData);
                } else {
                    flowmetryIds.forEach(flowmetryId => {
                        const { visible, ...otherData } = getPreviousFlowmetryResearch({
                            tabletList: list,
                            wellId,
                            flowmetryId,
                        });
                        let newVisible = visible;
                        if (isEmpty) {
                            newVisible = true;
                        }
                        wellData.flowmetry[wellId][flowmetryId] = { ...otherData, visible: newVisible };
                    });
                    this.updateTablets(wellData);
                }

                // const { visible, header, ...otherData } = getPreviousFlowmetryResearch({
                //     tabletList: list,
                //     wellId,
                //     flowmetryId,
                // });

                // this.updateTablets({
                //     activeGroupName,
                //     activeTabletId,
                //     flowmetry: { [activeTabletId]: { [flowmetryId]: { visible: !visible, header, ...otherData } } },
                // });
            }
        },

        removeTablet(payload, rootState) {
            const removedKey = payload;
            const { tablets } = rootState;
            const { list, wellsMetaData, activeGroupName } = tablets;

            if (
                list &&
                activeGroupName &&
                Object.entries(list[activeGroupName]).some(([, v]) => v?.key === removedKey)
            ) {
                const removedGroup = Object.fromEntries(
                    Object.entries(list[activeGroupName]).filter(([, v]) => v?.key !== removedKey),
                );
                const { [activeGroupName]: _discard, ...otherList } = list;

                this.updateList({ ...otherList, [activeGroupName]: removedGroup });
                return;
            }

            Object.entries(wellsMetaData).forEach(listItem => {
                const [name, data] = listItem;

                const isWell = 'title' in data;

                if (isWell && data && data?.key === removedKey) {
                    const { [name]: removedWell, ...otherListData } = list;

                    this.updateList(otherListData);
                    return;
                }
                if (data) {
                    Object.entries(data).forEach(groupItem => {
                        const [groupName, dataInGroup] = listItem;

                        Object.entries(dataInGroup).forEach(groupWell => {
                            const [id, data] = groupWell;

                            if (data?.key === removedKey) {
                                const {
                                    [groupName]: { [id]: removedWell, ...otherGroup },
                                    ...otherList
                                } = list;

                                const listToUpdate = { [groupName]: { ...otherGroup }, ...otherList };

                                dispatch.inspector.resetGroupGeoObjectsList();

                                this.updateList(listToUpdate);
                                return;
                            }
                        });
                    });
                }
            });
        },
        toggleItemGroupIsCheck(payload, rootSate) {
            this.toggleTabletInGroupSelected(payload);
        },
        toggleIsAddCustomGeoversionActive(payload) {
            const { isAddCustomGeoVersionActive } = payload;
            this.setIsAddCustomGeoVersionActive(!isAddCustomGeoVersionActive);
        },
        toggleIsEditCustomGeoversionActive(payload) {
            const { isEditCustomGeoVersionActive } = payload;
            this.setIsEditCustomGeoVersionActive(!isEditCustomGeoVersionActive);
        },

        toggleNewCustomGeoObjects(payload, rootState) {
            const { lists, tablets } = rootState;
            const { geoObject } = payload;
            const { activeItemKey, data: listItems } = lists;

            const { newCustomGeoObjects } = tablets;
            let filteredCustomGeoObjects = [...newCustomGeoObjects];

            const existsSameTrue = filteredCustomGeoObjects.some(
                obj => obj.trueBoredStratumID === geoObject.trueBoredStratumID,
            );

            if (existsSameTrue) {
                filteredCustomGeoObjects = filteredCustomGeoObjects.filter(
                    obj => !(obj.trueBoredStratumID === geoObject.trueBoredStratumID),
                );
            } else {
                filteredCustomGeoObjects = filteredCustomGeoObjects.filter(obj => {
                    const sameBoredStratumAndWell =
                        obj.boredStratumId === geoObject.boredStratumId && obj.wellId === geoObject.wellId;

                    return !sameBoredStratumAndWell;
                });

                filteredCustomGeoObjects = [geoObject, ...filteredCustomGeoObjects];
            }

            this.setNewCustomGeoObjects(filteredCustomGeoObjects);
        },

        toggleCustomGeoObjects(payload, rootState) {
            const { lists, tablets, inspector } = rootState;
            const { geoObject } = payload;
            const { activeItemKey, data: listItems } = lists;
            const { geoObjects } = inspector;
            const { customGeoObjects, activeGroupName, activeTabletId } = tablets;

            const activeVersion = geoObjects?.[activeGroupName || activeTabletId][0];
            let filteredCustomGeoObjects = customGeoObjects.filter(
                obj => obj.versionId === activeVersion.stratumVersionId || obj.isNew,
            );

            const isSameGeoObject = filteredCustomGeoObjects.some(
                obj => obj.trueBoredStratumID === geoObject.trueBoredStratumID,
            );
            if (isSameGeoObject) {
                filteredCustomGeoObjects = filteredCustomGeoObjects.filter(
                    obj => obj.trueBoredStratumID !== geoObject.trueBoredStratumID,
                );
            } else {
                filteredCustomGeoObjects = filteredCustomGeoObjects.filter(obj => {
                    const sameBoredStratumAndWell =
                        obj.boredStratumId === geoObject.boredStratumId && obj.wellId === geoObject.wellId;

                    return !sameBoredStratumAndWell;
                });

                filteredCustomGeoObjects = [geoObject, ...filteredCustomGeoObjects];
            }

            this.setCustomGeoObjects(filteredCustomGeoObjects);
        },
        addNewCustomGeoObject(payload, rootState) {
            const { lists, tablets } = rootState;
            const { newCustomGeoObject, isAddCustomGeoVersionActive } = payload;
            const { activeItemKey, data: listItems } = lists;
            const { customGeoObjects, newCustomGeoObjects } = tablets;

            if (isAddCustomGeoVersionActive) {
                this.setNewCustomGeoObjects([newCustomGeoObject, ...newCustomGeoObjects]);
            } else {
                this.setCustomGeoObjects([newCustomGeoObject, ...customGeoObjects]);
            }
        },
        deleteNewCustomGeoObject(payload, rootState) {
            const { lists, tablets } = rootState;
            const { id, isAddCustomGeoVersionActive } = payload;
            const { activeItemKey, data: listItems } = lists;
            const { customGeoObjects, newCustomGeoObjects } = tablets;
            const filteredCustomGeoObjects = isAddCustomGeoVersionActive
                ? newCustomGeoObjects.filter(obj => obj.id !== id)
                : customGeoObjects.filter(obj => obj.id !== id);

            if (isAddCustomGeoVersionActive) {
                this.setNewCustomGeoObjects(filteredCustomGeoObjects);
            } else {
                this.setCustomGeoObjects(filteredCustomGeoObjects);
            }
        },
        updateNewCustomGeoObject(payload, rootState) {
            const { lists, tablets } = rootState;
            const { newCustomGeoObject, isAddCustomGeoVersionActive } = payload;
            const { activeItemKey, data: listItems } = lists;
            const { customGeoObjects, newCustomGeoObjects } = tablets;
            const filteredCustomGeoObjects = isAddCustomGeoVersionActive
                ? newCustomGeoObjects.map(item => (item.id === newCustomGeoObject.id ? newCustomGeoObject : item))
                : customGeoObjects.map(item => (item.id === newCustomGeoObject.id ? newCustomGeoObject : item));

            if (isAddCustomGeoVersionActive) {
                this.setNewCustomGeoObjects(filteredCustomGeoObjects);
            } else {
                this.setCustomGeoObjects(filteredCustomGeoObjects);
            }
        },

        removeNewCustomGeoObjects(payload, rootState) {
            this.setNewCustomGeoObjects([]);
        },
        removeCustomGeoObjectsByWell(payload, rootState) {
            const { tablets } = rootState;
            const { wellId } = payload;
            const { customGeoObjects } = tablets;
            const filteredCustomGeoObjects = customGeoObjects.filter(obj => obj.wellId !== wellId);
            this.setCustomGeoObjects(filteredCustomGeoObjects);
        },
        removeNewCustomGeoObjectsByWell(payload, rootState) {
            const { tablets } = rootState;
            const { wellId } = payload;
            const { newCustomGeoObjects } = tablets;
            const filteredCustomGeoObjects = newCustomGeoObjects.filter(obj => obj.wellId !== wellId);

            this.setNewCustomGeoObjects(filteredCustomGeoObjects);
        },
        updateCustomGeoObjects(payload, rootState) {
            const { tablets, inspector } = rootState;
            const { customGeoObjects, activeGroupName, list, newCustomGeoObjects, isEditCustomGeoVersionActive } =
                tablets;
            const { allWellVersions } = inspector;
            const { boredStratumId, versionId, rowData, isNew } = payload;
            let selectedRow = null;

            const currentCustomGeoObjects = isNew
                ? newCustomGeoObjects
                : customGeoObjects.filter(obj => obj.versionId === rowData.versionId);
            const geoObjects = activeGroupName
                ? list[activeGroupName][rowData.wellId]?.geoObjects.versions
                : list[rowData.wellId].geoObjects.versions;
            const otherGeoObjects = customGeoObjects.filter(obj => obj.versionId !== rowData.versionId);

            if (boredStratumId) {
                const targetVersionId = isNew ? rowData.versionId : rowData.originVersionId;
                const geoObjectVersion = geoObjects?.find(ver => ver.versionId === targetVersionId);
                const existingStratumData = geoObjectVersion?.data?.find(
                    item => item.boredStratumId === boredStratumId,
                );

                if (existingStratumData) {
                    selectedRow = existingStratumData;
                } else if (allWellVersions?.[rowData.wellId]) {
                    const wellVersion = allWellVersions[rowData.wellId].find(
                        version => version.stratumVersionId === rowData.originVersionId,
                    );
                    selectedRow =
                        wellVersion?.stratums?.find(stratum => stratum.boredStratumId === boredStratumId) || null;
                }
            } else {
                const targetVersionId = isNew ? rowData.versionId : versionId;
                const geoObjectVersion = geoObjects?.find(ver => ver.versionId === targetVersionId)
                    ? geoObjects?.find(ver => ver.versionId === targetVersionId)
                    : allWellVersions[rowData.wellId].find(version => version.stratumVersionId === targetVersionId);
                selectedRow = geoObjectVersion?.data
                    ? geoObjectVersion?.data?.find(item => item.boredStratumId === rowData.boredStratumId)
                    : geoObjectVersion?.stratums?.find(item => item.boredStratumId === rowData.boredStratumId) || null;
            }

            const filteredCustomGeoObjects = currentCustomGeoObjects.map(row => {
                let selectedVersion = null;

                if (versionId) {
                    const geoObjectVersion = geoObjects?.find(ver => ver.versionId === versionId);

                    if (geoObjectVersion) {
                        selectedVersion = geoObjectVersion;
                    } else {
                        selectedVersion =
                            allWellVersions?.[rowData.wellId]?.find(
                                version => version.stratumVersionId === versionId,
                            ) || null;
                    }
                } else {
                    const targetVersionId = isNew ? rowData.versionId : rowData.originVersionId;
                    selectedVersion = geoObjects?.find(ver => ver.versionId === targetVersionId) || null;
                }

                if (row.trueBoredStratumID === rowData.trueBoredStratumID) {
                    return {
                        ...row,
                        boredStratumAbsBottom: selectedRow.boredStratumAbsBottom,
                        boredStratumAbsRoof: selectedRow.boredStratumAbsRoof,
                        boredStratumBottom: selectedRow.boredStratumBottom,
                        boredStratumId: selectedRow.boredStratumId,
                        boredStratumRoof: selectedRow.boredStratumRoof,
                        boredStratumTitle: selectedRow.boredStratumTitle,
                        children: selectedRow.children,
                        params: selectedRow.params,
                        trueBoredStratumID: selectedRow.trueBoredStratumID,
                        versionId:
                            isNew || (rowData.isNew && isEditCustomGeoVersionActive)
                                ? (versionId ?? rowData.versionId)
                                : row.versionId,
                        originVersionId: versionId ?? rowData.originVersionId,
                        originVersionName: selectedVersion
                            ? (selectedVersion?.projectDoc ??
                              selectedVersion.description ??
                              selectedVersion.stratumVersionName)
                            : rowData.originVersionName,
                        versionTitle:
                            isNew || (rowData.isNew && isEditCustomGeoVersionActive)
                                ? (selectedVersion?.projectDoc ?? selectedVersion.description)
                                : selectedRow?.versionTitle
                                  ? rowData.versionTitle
                                  : '',
                    };
                }
                return row;
            });

            if (isNew) this.setNewCustomGeoObjects(filteredCustomGeoObjects);
            else this.setCustomGeoObjects([...filteredCustomGeoObjects, ...otherGeoObjects]);
        },
    }),
    selectors: (slice, createSelector, hasProps) => ({
        wellGeoObjects() {
            return slice(tablets => {
                const { activeTabletId, activeGroupName, isGroup, list } = tablets;

                if (activeTabletId) {
                    const wellGeoObjects = list[activeTabletId].geoObjects.versions;

                    return {
                        [activeTabletId]: wellGeoObjects,
                    };
                }

                if (isGroup) {
                    const wells = list[activeGroupName];

                    const result = {};

                    Object.entries(wells).map(well => {
                        const [wellId, tablet] = well;

                        result[wellId] = tablet.geoObjects?.versions;
                    });

                    return result;
                }
            });
        },
        /*
            Вопросы:
            - сам selector тут лишний (постоянное изменение list не дает никакого кеширования)

            Так как все данные здесь хранятся в объектах, о порядке речи не может идти.
            Поэтому этот selector возвращает массив элементов, которые идут в необходимом порядке, и
            компонент с идентификатором.
        */
        wellFlowmetryList() {
            return slice(tablets => {
                const { activeTabletId, activeGroupName, isGroup, list } = tablets;

                if (activeTabletId) {
                    const wellFlowmetry = list[activeTabletId].flowmetry.columns;

                    const result = {};

                    if (!wellFlowmetry) {
                        result[activeTabletId] = null;

                        return result;
                    }

                    result[activeTabletId] = flowmetryNormalize.toList(wellFlowmetry);

                    return result;
                }

                if (isGroup) {
                    const wells = list[activeGroupName];

                    const result = {};

                    for (const well of Object.entries(wells)) {
                        const [wellId, tablet] = well;

                        const wellFlowmetry = tablet.flowmetry.columns;

                        if (!wellFlowmetry) {
                            result[wellId] = null;
                            continue;
                        }

                        result[wellId] = flowmetryNormalize.toList(wellFlowmetry);
                    }

                    return result;
                }
            });
        },
    }),
};

function getPreviousFlowmetryResearch({ tabletList, activeGroupName, wellId, flowmetryId }) {
    const prevFlowmetry = activeGroupName
        ? tabletList[activeGroupName]?.[wellId]?.flowmetry
        : tabletList[wellId]?.flowmetry;
    const prevStateResearch = prevFlowmetry?.columns?.[flowmetryId];

    return prevStateResearch;
}

/*
    Вспомогательные функции. Чтобы не засорять и так переполненную модель.
*/

/*
    Получить коллекцию минимальных и максимальных глубин.
    На вход принимает структуру вида: { [id]: [data] }.

    Используется для получения информации о глубине в перфорации и изоляции.
    min - roof
    max - bottom
    В дальнейшем результат этой функции будет использоваться в поиске мин и макс глубин, чтобы правильно отрисовать шкалу глубины на планшете.
*/
function getDepthFromData(dataObject) {
    const values = Object.values(dataObject);

    const minMaxCollections = values.map(item => {
        const depthValues = [];

        item.forEach(unit => depthValues.push(unit.roof, unit.bottom));

        return depthValues;
    });

    return minMaxCollections.flat();
}

export default tabletsModel;
